import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  BookOpen, 
  Target, 
  Brain, 
  FileText, 
  Edit3, 
  Search, 
  Filter, 
  Clock, 
  Star, 
  ChevronRight, 
  Play,
  BarChart3,
  Lightbulb,
  Users,
  Trophy,
  ArrowLeft,
  CheckCircle,
  XCircle,
  RotateCcw,
  RefreshCw,
  Shuffle,
  MessageCircle,
  Bot,
  Zap,
  HelpCircle
} from 'lucide-react';

// Import data (static data still used for text listings)
import { 
  hkdseTexts, 
  studyActivityTypes, 
  difficultyLevels, 
  getAllTexts, 
  getTextsByCategory, 
  getHKDSEStats 
} from '../data/hkdseTexts';

// Import unified data service (A2A with Supabase fallback)
import chineseDataService from '../services/chineseDataService';

// Import Supabase services for features not yet in A2A
import { 
  getHKDSEGradeLevel, 
  getHKDSEStudyRecommendations,
  getTypeBQuizByType,
  getTypeCQuizByType,
  getTypeDQuizByType,
  getTypeEQuizByType,
  getTypeFQuizByType
} from '../services/supabase/hkdseQuizService';
import { shuffleFlashCards } from '../services/supabase/hkdseFlashCardsService';
import { getCategoryQuizByType } from '../services/supabase/hkdseCategoryQuizService';

import DeepSeekChatWindow from './DeepSeekChatWindow';
import SoundButton from './SoundButton';

const HKDSETexts = () => {
  const [currentView, setCurrentView] = useState('home');
  const [selectedText, setSelectedText] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('category');
  
  // Quiz states
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizScore, setQuizScore] = useState({ correct: 0, total: 0 });
  const [quizResults, setQuizResults] = useState([]);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  
  // FlashCard states
  const [currentFlashCards, setCurrentFlashCards] = useState(null);
  const [currentCard, setCurrentCard] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [cardProgress, setCardProgress] = useState({ known: 0, learning: 0 });
  
  // Chat window states
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMode, setChatMode] = useState('direct');
  const [chatContext, setChatContext] = useState(null);
  
  // Loading and error states
  const [loadingStates, setLoadingStates] = useState({
    quiz: false,
    flashCards: false,
    originalText: false,
    categoryQuiz: false,
    qa: false
  });
  const [errorStates, setErrorStates] = useState({
    quiz: null,
    flashCards: null,
    originalText: null,
    categoryQuiz: null,
    qa: null
  });

  // Get filtered texts
  const allTexts = getAllTexts();
  const filteredTexts = allTexts.filter(text => {
    const matchesSearch = text.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         text.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         text.mainTheme.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || text.categoryKey === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Sort texts
  const sortedTexts = [...filteredTexts].sort((a, b) => {
    switch (sortBy) {
      case 'difficulty':
        const diffOrder = { beginner: 0, intermediate: 1, advanced: 2 };
        return diffOrder[a.difficulty] - diffOrder[b.difficulty];
      case 'studyTime':
        return a.studyTime - b.studyTime;
      case 'dynasty':
        return a.dynasty.localeCompare(b.dynasty);
      default:
        return a.categoryKey.localeCompare(b.categoryKey);
    }
  });

  const stats = getHKDSEStats();

  // Timer effect for quiz
  useEffect(() => {
    let timer;
    if (currentView === 'quiz' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [currentView, timeLeft]);

  const handleTimeUp = () => {
    if (currentQuiz && currentQuestion < currentQuiz.questions.length) {
      handleQuizAnswer(null); // Submit with no answer
    }
  };

  const startQuiz = async (textId) => {
    setLoadingStates(prev => ({ ...prev, quiz: true }));
    setErrorStates(prev => ({ ...prev, quiz: null }));
    
    try {
      // Use unified service (A2A with Supabase fallback)
      const quiz = await chineseDataService.getHKDSEQuiz(textId);
      if (quiz) {
        setCurrentQuiz(quiz);
        setCurrentQuestion(0);
        setSelectedAnswer(null);
        setQuizScore({ correct: 0, total: 0 });
        setQuizResults([]);
        setShowQuizResult(false);
        setTimeLeft(quiz.timeLimit);
        setCurrentView('quiz');
      } else {
        setErrorStates(prev => ({ ...prev, quiz: '找不到測驗資料' }));
      }
    } catch (error) {
      console.error('Error loading quiz:', error);
      setErrorStates(prev => ({ ...prev, quiz: '載入測驗時發生錯誤' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, quiz: false }));
    }
  };

  const startCategoryQuiz = async (textId, categoryType) => {
    setLoadingStates(prev => ({ ...prev, categoryQuiz: true }));
    setErrorStates(prev => ({ ...prev, categoryQuiz: null }));
    
    try {
      // Try to get quiz from different type data files
      let categoryQuiz = await getCategoryQuizByType(textId, categoryType);
      
      if (!categoryQuiz) {
        // Try Type B quiz data
        categoryQuiz = await getTypeBQuizByType(textId, categoryType);
      }
      
      if (!categoryQuiz) {
        // Try Type C quiz data
        categoryQuiz = await getTypeCQuizByType(textId, categoryType);
      }
      
      if (!categoryQuiz) {
        // Try Type D quiz data
        categoryQuiz = await getTypeDQuizByType(textId, categoryType);
      }
      
      if (!categoryQuiz) {
        // Try Type E quiz data
        categoryQuiz = await getTypeEQuizByType(textId, categoryType);
      }
      
      if (!categoryQuiz) {
        // Try Type F quiz data
        categoryQuiz = await getTypeFQuizByType(textId, categoryType);
      }

      if (categoryQuiz) {
        setCurrentQuiz(categoryQuiz);
        setCurrentQuestion(0);
        setSelectedAnswer(null);
        setQuizScore({ correct: 0, total: 0 });
        setQuizResults([]);
        setShowQuizResult(false);
        setTimeLeft(categoryQuiz.timeLimit);
        setCurrentView('quiz');
      } else {
        setErrorStates(prev => ({ ...prev, categoryQuiz: '找不到分類測驗資料' }));
      }
    } catch (error) {
      console.error('Error loading category quiz:', error);
      setErrorStates(prev => ({ ...prev, categoryQuiz: '載入分類測驗時發生錯誤' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, categoryQuiz: false }));
    }
  };

  const handleQuizAnswer = (answerIndex) => {
    const question = currentQuiz.questions[currentQuestion];
    const isCorrect = answerIndex === question.correct;
    
    const result = {
      questionId: question.id,
      question: question.question,
      selectedAnswer: answerIndex !== null ? question.options[answerIndex] : '未作答',
      correctAnswer: question.options[question.correct],
      isCorrect,
      explanation: question.explanation,
      points: isCorrect ? question.points : 0
    };
    
    setQuizResults(prev => [...prev, result]);
    setQuizScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
    
    setSelectedAnswer(answerIndex);
    setShowQuizResult(true);
    
    setTimeout(() => {
      if (currentQuestion < currentQuiz.questions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
        setSelectedAnswer(null);
        setShowQuizResult(false);
      } else {
        setCurrentView('quizCompleted');
      }
    }, 2000);
  };

  const startFlashCards = async (textId) => {
    setLoadingStates(prev => ({ ...prev, flashCards: true }));
    setErrorStates(prev => ({ ...prev, flashCards: null }));
    
    try {
      // Use unified service (A2A with Supabase fallback)
      const flashCardSet = await chineseDataService.getHKDSEFlashCards(textId);
      if (flashCardSet) {
        setCurrentFlashCards(flashCardSet);
        setCurrentCard(0);
        setShowAnswer(false);
        setCardProgress({ known: 0, learning: 0 });
        setCurrentView('flashcards');
      } else {
        setErrorStates(prev => ({ ...prev, flashCards: '找不到學習卡片資料' }));
      }
    } catch (error) {
      console.error('Error loading flash cards:', error);
      setErrorStates(prev => ({ ...prev, flashCards: '載入學習卡片時發生錯誤' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, flashCards: false }));
    }
  };

  const handleCardAction = (action) => {
    if (action === 'known') {
      setCardProgress(prev => ({ ...prev, known: prev.known + 1 }));
    } else if (action === 'learning') {
      setCardProgress(prev => ({ ...prev, learning: prev.learning + 1 }));
    }
    
    if (currentCard < currentFlashCards.cards.length - 1) {
      setCurrentCard(prev => prev + 1);
      setShowAnswer(false);
    } else {
      setCurrentView('flashcardsCompleted');
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Chat functions
  const openChat = (mode = 'direct', context = null) => {
    setChatMode(mode);
    setChatContext(context);
    setIsChatOpen(true);
  };

  const closeChat = () => {
    setIsChatOpen(false);
    setChatMode('direct');
    setChatContext(null);
  };

  // Home View
  const HomeView = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white">
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2 text-slate-100">HKDSE 中文科</h1>
          <h2 className="text-2xl font-bold mb-4 text-slate-200">十二篇範文學習系統</h2>
          <p className="text-slate-300 text-lg">深入理解經典文學，掌握考試重點</p>
        </div>

        <div className="bg-white rounded-3xl p-8 text-gray-800 mb-8">
          {/* Search and Filter */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜尋範文標題、作者或主旨..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">所有類別</option>
                {Object.entries(hkdseTexts).map(([key, category]) => (
                  <option key={key} value={key}>{category.category}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="category">按類別排序</option>
                <option value="difficulty">按難度排序</option>
                <option value="studyTime">按學習時間排序</option>
                <option value="dynasty">按朝代排序</option>
              </select>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-indigo-50 rounded-xl p-4 text-center border border-indigo-100">
              <BookOpen className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
              <div className="text-2xl font-bold text-indigo-600">{stats.totalTexts}</div>
              <div className="text-sm text-gray-600">篇範文</div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
              <Target className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
              <div className="text-2xl font-bold text-emerald-600">{stats.totalCategories}</div>
              <div className="text-sm text-gray-600">個類別</div>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 text-center border border-amber-100">
              <Clock className="w-8 h-8 mx-auto mb-2 text-amber-600" />
              <div className="text-2xl font-bold text-amber-600">{stats.totalStudyTime}</div>
              <div className="text-sm text-gray-600">分鐘學習</div>
            </div>
            <div className="bg-violet-50 rounded-xl p-4 text-center border border-violet-100">
              <Trophy className="w-8 h-8 mx-auto mb-2 text-violet-600" />
              <div className="text-2xl font-bold text-violet-600">{filteredTexts.length}</div>
              <div className="text-sm text-gray-600">搜尋結果</div>
            </div>
          </div>

          {/* Text List */}
          <div className="space-y-4">
            {sortedTexts.map((text) => (
              <div key={text.id} className="border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-800">{text.textKey}. {text.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${difficultyLevels[text.difficulty].color}`}>
                        {difficultyLevels[text.difficulty].name}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <span>📚 {text.categoryName}</span>
                      <span>✍️ {text.author}</span>
                      <span>🏛️ {text.dynasty}</span>
                      <span>⏱️ {text.studyTime}分鐘</span>
                    </div>
                    <p className="text-gray-700 text-sm line-clamp-3">{text.mainTheme}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => startQuiz(text.id)}
                    disabled={loadingStates.quiz}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingStates.quiz ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>載入中...</span>
                      </>
                    ) : (
                      <>
                        <Target className="w-4 h-4" />
                        <span>測驗</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => startFlashCards(text.id)}
                    disabled={loadingStates.flashCards}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingStates.flashCards ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>載入中...</span>
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4" />
                        <span>學習卡片</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedText(text);
                      setCurrentView('notes');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-sm"
                  >
                    <FileText className="w-4 h-4" />
                    <span>學習筆記</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedText(text);
                      setCurrentView('analysis');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-xl hover:bg-slate-700 transition-colors shadow-sm"
                  >
                    <Search className="w-4 h-4" />
                    <span>文本分析</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedText(text);
                      setCurrentView('qa');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors shadow-sm"
                  >
                    <Lightbulb className="w-4 h-4" />
                    <span>問答題精選</span>
                  </button>
                  <button
                    onClick={() => openChat('direct', text)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-colors shadow-sm"
                  >
                    <Bot className="w-4 h-4" />
                    <span>AI助手</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Floating Chat Button */}
        <div className="fixed bottom-6 right-6 z-40">
          <div className="relative">
            <button
              onClick={() => openChat('direct', null)}
              className="w-14 h-14 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full shadow-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 flex items-center justify-center hover:scale-110"
            >
              <MessageCircle className="w-6 h-6" />
            </button>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Quiz View
  const QuizView = () => {
    if (!currentQuiz) return <div>Loading...</div>;
    
    const question = currentQuiz.questions[currentQuestion];
    const progress = ((currentQuestion + 1) / currentQuiz.questions.length) * 100;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800 text-white">
        <div className="max-w-4xl mx-auto p-6">
                      <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2">{currentQuiz.title}</h1>
              <div className="text-emerald-100">
                第 {currentQuestion + 1} 題，共 {currentQuiz.questions.length} 題
              </div>
            </div>

          <div className="bg-white rounded-3xl p-8 text-gray-800">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => setCurrentView('home')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-xl hover:bg-gray-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回</span>
              </button>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  剩餘時間: {formatTime(timeLeft)}
                </div>
                <div className="text-sm text-gray-600">
                  分數: {quizScore.correct}/{quizScore.total}
                </div>
                <button
                  onClick={() => openChat('quiz', currentQuiz)}
                  className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-colors text-sm"
                >
                  <Zap className="w-4 h-4" />
                  <span>AI測驗</span>
                </button>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
              <div 
                className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                style={{width: `${progress}%`}}
              ></div>
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4">{question.question}</h3>
              <div className="grid gap-3">
                {question.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => !showQuizResult && handleQuizAnswer(index)}
                    disabled={showQuizResult}
                    className={`p-4 text-left rounded-xl border-2 transition-all ${
                      showQuizResult
                        ? index === question.correct
                          ? 'bg-green-100 border-green-500 text-green-800'
                          : selectedAnswer === index
                          ? 'bg-red-100 border-red-500 text-red-800'
                          : 'bg-gray-50 border-gray-200'
                        : selectedAnswer === index
                        ? 'bg-blue-100 border-blue-500'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        showQuizResult
                          ? index === question.correct
                            ? 'bg-green-500 text-white'
                            : selectedAnswer === index
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-300 text-gray-600'
                          : selectedAnswer === index
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </div>
                      <span>{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {showQuizResult && (
              <div className={`p-4 rounded-xl mb-4 ${
                selectedAnswer === question.correct
                  ? 'bg-green-100 border border-green-300'
                  : 'bg-red-100 border border-red-300'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {selectedAnswer === question.correct ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className="font-bold">
                    {selectedAnswer === question.correct ? '回答正確！' : '回答錯誤'}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{question.explanation}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Quiz Completed View
  const QuizCompletedView = () => {
    const finalScore = Math.round((quizScore.correct / quizScore.total) * 100);
    const gradeLevel = getHKDSEGradeLevel(finalScore);
    const recommendations = getHKDSEStudyRecommendations(finalScore, currentQuiz.category);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto p-6">
                      <div className="text-center mb-8">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-amber-400" />
              <h1 className="text-3xl font-bold mb-2">測驗完成！</h1>
              <p className="text-indigo-100">恭喜完成 {currentQuiz.title}</p>
            </div>

          <div className="bg-white rounded-3xl p-8 text-gray-800">
            <div className="text-center mb-8">
              <div className={`inline-block px-6 py-3 rounded-2xl text-2xl font-bold ${gradeLevel.bgColor} ${gradeLevel.color}`}>
                {finalScore}% - {gradeLevel.label}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{quizScore.correct}</div>
                  <div className="text-sm text-gray-600">答對題數</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{quizScore.total - quizScore.correct}</div>
                  <div className="text-sm text-gray-600">答錯題數</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{quizScore.total}</div>
                  <div className="text-sm text-gray-600">總題數</div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5" />
                學習建議
              </h3>
              <div className="space-y-2">
                {recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <p className="text-gray-700">{rec}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setCurrentQuestion(0);
                  setQuizScore({ correct: 0, total: 0 });
                  setQuizResults([]);
                  setShowQuizResult(false);
                  setTimeLeft(currentQuiz.timeLimit);
                  setCurrentView('quiz');
                }}
                className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span>重新測驗</span>
              </button>
              <button
                onClick={() => setCurrentView('home')}
                className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回首頁</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // FlashCards View
  const FlashCardsView = () => {
    if (!currentFlashCards) return <div>Loading...</div>;
    
    const card = currentFlashCards.cards[currentCard];
    const progress = ((currentCard + 1) / currentFlashCards.cards.length) * 100;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-600 via-violet-700 to-violet-800 text-white">
        <div className="max-w-4xl mx-auto p-6">
                      <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2">{currentFlashCards.title}</h1>
              <div className="text-violet-100">
                第 {currentCard + 1} 張，共 {currentFlashCards.cards.length} 張
              </div>
            </div>

          <div className="bg-white rounded-3xl p-8 text-gray-800">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => setCurrentView('home')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-xl hover:bg-gray-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回</span>
              </button>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  已掌握: {cardProgress.known}
                </div>
                <div className="text-sm text-gray-600">
                  學習中: {cardProgress.learning}
                </div>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
              <div 
                className="bg-violet-500 h-2 rounded-full transition-all duration-300"
                style={{width: `${progress}%`}}
              ></div>
            </div>

            <div 
              className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl p-8 min-h-64 flex flex-col justify-center items-center cursor-pointer transition-transform hover:scale-105 mb-6"
              onClick={() => setShowAnswer(!showAnswer)}
            >
              {!showAnswer ? (
                <div className="text-center">
                  <div className="text-lg font-bold mb-4 text-violet-600">問題</div>
                  <p className="text-xl font-semibold text-gray-800">{card.front}</p>
                  <p className="text-sm text-gray-500 mt-6">點擊查看答案</p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-lg font-bold mb-4 text-emerald-600">答案</div>
                  <p className="text-xl font-semibold text-gray-800 mb-4">{card.back}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full">
                      {card.category}
                    </span>
                    <span className={`px-2 py-1 rounded-full ${difficultyLevels[card.difficulty].color}`}>
                      {difficultyLevels[card.difficulty].name}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {showAnswer && (
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => handleCardAction('learning')}
                  className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-white rounded-xl hover:bg-yellow-600 transition-colors"
                >
                  <Brain className="w-4 h-4" />
                  <span>還在學習</span>
                </button>
                <button
                  onClick={() => handleCardAction('known')}
                  className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>已經掌握</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Notes View
  const NotesView = () => {
    if (!selectedText) return <div>Loading...</div>;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto p-6">
                      <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2">{selectedText.title}</h1>
              <p className="text-slate-300">{selectedText.author} - {selectedText.dynasty}</p>
            </div>

          <div className="bg-white rounded-3xl p-8 text-gray-800">
            <div className="mb-6">
              <button
                onClick={() => setCurrentView('home')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-xl hover:bg-gray-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回</span>
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-indigo-600" />
                  主旨
                </h3>
                <div className="bg-indigo-50 p-4 rounded-xl mb-6">
                  <p className="text-gray-700">{selectedText.mainTheme}</p>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-600" />
                    重點概念
                  </h3>
                  <button
                    onClick={() => startCategoryQuiz(selectedText.id, 'keyPoints')}
                    disabled={loadingStates.categoryQuiz}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingStates.categoryQuiz ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>載入中...</span>
                      </>
                    ) : (
                      <>
                        <Target className="w-4 h-4" />
                        <span>Quiz</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="space-y-2 mb-6">
                  {selectedText.keyPoints.map((point, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
                      <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <p className="text-gray-700">{point}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-emerald-600" />
                  考核重點
                </h3>
                <div className="space-y-4">
                  {selectedText.examFocus.argumentationMethods && (
                    <div className="bg-emerald-50 p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-emerald-800">論證手法</h4>
                        <button
                          onClick={() => startCategoryQuiz(selectedText.id, 'argumentationMethods')}
                          disabled={loadingStates.categoryQuiz}
                          className="flex items-center gap-2 px-3 py-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingStates.categoryQuiz ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Target className="w-3 h-3" />
                              <span>Quiz</span>
                            </>
                          )}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedText.examFocus.argumentationMethods.map((method, index) => (
                          <span key={index} className="px-3 py-1 bg-emerald-200 text-emerald-800 rounded-full text-sm">
                            {method}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedText.examFocus.writingTechniques && (
                    <div className="bg-violet-50 p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-violet-800">寫作手法</h4>
                        <button
                          onClick={() => startCategoryQuiz(selectedText.id, 'writingTechniques')}
                          disabled={loadingStates.categoryQuiz}
                          className="flex items-center gap-2 px-3 py-1 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingStates.categoryQuiz ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Target className="w-3 h-3" />
                              <span>Quiz</span>
                            </>
                          )}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedText.examFocus.writingTechniques.map((technique, index) => (
                          <span key={index} className="px-3 py-1 bg-violet-200 text-violet-800 rounded-full text-sm">
                            {technique}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedText.examFocus.rhetoricalDevices && (
                    <div className="bg-rose-50 p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-rose-800">修辭手法</h4>
                        <button
                          onClick={() => startCategoryQuiz(selectedText.id, 'rhetoricalDevices')}
                          disabled={loadingStates.categoryQuiz}
                          className="flex items-center gap-2 px-3 py-1 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingStates.categoryQuiz ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Target className="w-3 h-3" />
                              <span>Quiz</span>
                            </>
                          )}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedText.examFocus.rhetoricalDevices.map((device, index) => (
                          <span key={index} className="px-3 py-1 bg-rose-200 text-rose-800 rounded-full text-sm">
                            {device}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-bold text-gray-800 mb-2">學習資訊</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>類別：</span>
                      <span>{selectedText.categoryName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>難度：</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${difficultyLevels[selectedText.difficulty].color}`}>
                        {difficultyLevels[selectedText.difficulty].name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>建議學習時間：</span>
                      <span>{selectedText.studyTime} 分鐘</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Analysis View
  const AnalysisView = () => {
    const [originalTextData, setOriginalTextData] = useState(null);
    const [isLoadingLocal, setIsLoadingLocal] = useState(false);
    const [errorLocal, setErrorLocal] = useState(null);
    const dataCacheRef = useRef({});
    const loadedTextIdRef = useRef(null);
    const loadingRef = useRef(false);
    
    // Get stable text ID - must be before any returns
    const textId = selectedText?.id || null;
    
    // Calculate loading and error states - must be before any returns
    const isLoading = isLoadingLocal && !originalTextData;
    const hasError = errorLocal && !isLoadingLocal;
    
    useEffect(() => {
      if (!textId) {
        setOriginalTextData(null);
        setIsLoadingLocal(false);
        setErrorLocal(null);
        loadedTextIdRef.current = null;
        loadingRef.current = false;
        return;
      }
      
      // Already loaded this text ID - check cache first to avoid re-renders
      if (loadedTextIdRef.current === textId) {
        return;
      }
      
      // Check if we already have cached data for this ID
      if (dataCacheRef.current[textId]) {
        setOriginalTextData(dataCacheRef.current[textId]);
        setIsLoadingLocal(false);
        setErrorLocal(null);
        loadedTextIdRef.current = textId;
        loadingRef.current = false;
        return;
      }
      
      // Prevent duplicate loads
      if (loadingRef.current) {
        return;
      }
      
      loadingRef.current = true;
      let ignore = false;
      
      // Load the data following React best practices with cleanup
      async function startFetching() {
        setIsLoadingLocal(true);
        setErrorLocal(null);
        setOriginalTextData(null);
        
        try {
          // Use unified service (A2A with Supabase fallback)
          const data = await chineseDataService.getHKDSEOriginalText(textId);
          if (!ignore) {
            if (data) {
              dataCacheRef.current[textId] = data;
              setOriginalTextData(data);
              loadedTextIdRef.current = textId;
            } else {
              setErrorLocal('找不到原文資料');
            }
            setIsLoadingLocal(false);
            loadingRef.current = false;
          }
        } catch (error) {
          if (!ignore) {
            console.error('Error loading original text:', error);
            setErrorLocal('載入原文時發生錯誤，請稍後再試');
            setIsLoadingLocal(false);
            loadingRef.current = false;
          }
        }
      }
      
      startFetching();
      
      // Cleanup function to prevent race conditions
      return () => {
        ignore = true;
        loadingRef.current = false;
      };
    }, [textId]);
    
    if (!selectedText) return <div className="p-6 text-center text-white">載入中...</div>;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 text-white relative">
        {/* Loading Overlay - only shows while actively loading, doesn't replace content */}
        {isLoading && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center transition-opacity duration-200"
            key="loading-overlay"
          >
            <div className="text-center bg-white/10 rounded-xl p-8 backdrop-blur-md">
              <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-white" />
              <p className="text-white">載入原文中...</p>
            </div>
          </div>
        )}
        
        {/* Error Overlay - shows error without replacing content */}
        {hasError && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center transition-opacity duration-200"
            key="error-overlay"
          >
            <div className="text-center bg-red-900/90 rounded-xl p-8 backdrop-blur-md max-w-md">
              <XCircle className="w-8 h-8 mx-auto mb-4 text-white" />
              <p className="text-white">{errorLocal}</p>
              <button
                onClick={() => {
                  const textId = selectedText?.id;
                  if (textId) {
                    // Clear cache and error to allow retry
                    setErrorStates(prev => ({ ...prev, originalText: null }));
                    setOriginalTextData(null);
                    delete dataCacheRef.current[textId];
                  }
                }}
                className="mt-4 px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
              >
                重試
              </button>
            </div>
          </div>
        )}
        
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">文本分析 - {selectedText.title}</h1>
            <p className="text-slate-300">{selectedText.author} - {selectedText.dynasty}</p>
          </div>

          <div className="bg-white rounded-3xl p-8 text-gray-800">
            <div className="mb-6">
              <button
                onClick={() => setCurrentView('home')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-xl hover:bg-gray-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回</span>
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    原文內容
                  </h3>
                  {originalTextData && originalTextData.originalText && (
                    <SoundButton 
                      text={originalTextData.originalText}
                      title="播放原文"
                      className="flex-shrink-0"
                    />
                  )}
                </div>
                <div className="bg-indigo-50 p-6 rounded-xl mb-6 max-h-96 overflow-y-auto">
                  <div className="text-gray-700 leading-relaxed">
                    <p className="mb-4 text-center font-bold text-lg">{selectedText.title}</p>
                    <p className="text-sm text-gray-600 text-center mb-6">{selectedText.author}</p>
                    <div className="space-y-3">
                      {originalTextData && originalTextData.originalText ? (
                        <div className="whitespace-pre-line text-sm leading-relaxed">
                          {originalTextData.originalText}
                        </div>
                      ) : (
                        <div className="text-gray-500 italic">
                          <p>原文內容將從數據庫載入...</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Modern Translation */}
                {originalTextData && originalTextData.modernTranslation && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-emerald-600" />
                        現代語譯
                      </h3>
                      <SoundButton 
                        text={originalTextData.modernTranslation}
                        title="播放譯文"
                        className="flex-shrink-0"
                      />
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-xl max-h-64 overflow-y-auto">
                      <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                        {originalTextData.modernTranslation}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-emerald-600" />
                  文本分析
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-emerald-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-emerald-800">文體特色</h4>
                      <SoundButton 
                        text={`${selectedText.category} - 運用${selectedText.examFocus.argumentationMethods?.join('、')}等手法`}
                        title="播放"
                        className="flex-shrink-0"
                      />
                    </div>
                    <p className="text-gray-700 text-sm">
                      {selectedText.category} - 運用{selectedText.examFocus.argumentationMethods?.join('、')}等手法
                    </p>
                  </div>
                  
                  <div className="bg-violet-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-violet-800">主要論點</h4>
                      <SoundButton 
                        text={selectedText.mainTheme}
                        title="播放"
                        className="flex-shrink-0"
                      />
                    </div>
                    <p className="text-gray-700 text-sm">{selectedText.mainTheme}</p>
                  </div>
                  
                  <div className="bg-amber-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-amber-800">寫作技巧</h4>
                      <SoundButton 
                        text={selectedText.examFocus.writingTechniques?.join('、') || '寫作技巧'}
                        title="播放"
                        className="flex-shrink-0"
                      />
                    </div>
                    <div className="space-y-2">
                      {selectedText.examFocus.writingTechniques?.map((technique, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                          <span className="text-gray-700 text-sm">{technique}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-rose-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-rose-800">修辭手法</h4>
                      <SoundButton 
                        text={selectedText.examFocus.rhetoricalDevices?.join('、') || '修辭手法'}
                        title="播放"
                        className="flex-shrink-0"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedText.examFocus.rhetoricalDevices?.map((device, index) => (
                        <span key={index} className="px-2 py-1 bg-rose-200 text-rose-800 rounded-full text-xs">
                          {device}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => openChat('drill', selectedText)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-colors"
              >
                <Bot className="w-5 h-5" />
                <span>AI深度分析</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Q&A View
  const QAView = () => {
    const [qaData, setQaData] = useState(null);
    const [isLoadingLocal, setIsLoadingLocal] = useState(false);
    const [errorLocal, setErrorLocal] = useState(null);
    const qaCacheRef = useRef({});
    const loadedTextIdRef = useRef(null);
    const loadingRef = useRef(false);
    
    // Get stable text ID
    const textId = selectedText?.id || null;
    
    useEffect(() => {
      if (!textId) {
        setQaData(null);
        setIsLoadingLocal(false);
        setErrorLocal(null);
        loadedTextIdRef.current = null;
        loadingRef.current = false;
        return;
      }
      
      // Already loaded this text ID
      if (loadedTextIdRef.current === textId) {
        return;
      }
      
      // Check cache first
      if (qaCacheRef.current[textId]) {
        setQaData(qaCacheRef.current[textId]);
        setIsLoadingLocal(false);
        setErrorLocal(null);
        loadedTextIdRef.current = textId;
        loadingRef.current = false;
        return;
      }
      
      // Prevent duplicate loads
      if (loadingRef.current) {
        return;
      }
      
      loadingRef.current = true;
      let ignore = false;
      
      // Load the data following React best practices with cleanup
      async function startFetching() {
        setIsLoadingLocal(true);
        setErrorLocal(null);
        setQaData(null);
        
        try {
          // Use unified service (A2A with Supabase fallback)
          const data = await chineseDataService.getHKDSEQA(textId);
          if (!ignore) {
            if (data) {
              qaCacheRef.current[textId] = data;
              setQaData(data);
              loadedTextIdRef.current = textId;
            } else {
              setErrorLocal('找不到問答題資料');
            }
            setIsLoadingLocal(false);
            loadingRef.current = false;
          }
        } catch (error) {
          if (!ignore) {
            console.error('Error loading Q&A data:', error);
            setErrorLocal('載入問答題時發生錯誤，請稍後再試');
            setIsLoadingLocal(false);
            loadingRef.current = false;
          }
        }
      }
      
      startFetching();
      
      // Cleanup function to prevent race conditions
      return () => {
        ignore = true;
        loadingRef.current = false;
      };
    }, [textId]);
    
    if (!selectedText) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-orange-600 via-orange-700 to-orange-800 text-white flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
            <p>載入中...</p>
          </div>
        </div>
      );
    }
    if (isLoadingLocal) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-orange-600 via-orange-700 to-orange-800 text-white flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
            <p>載入問答題中...</p>
          </div>
        </div>
      );
    }
    if (errorLocal) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-orange-600 via-orange-700 to-orange-800 text-white flex items-center justify-center">
          <div className="text-center bg-red-900/50 p-6 rounded-xl">
            <XCircle className="w-8 h-8 mx-auto mb-4" />
            <p>{errorLocal}</p>
            <button
              onClick={() => {
                const textId = selectedText?.id;
                if (textId) {
                  // Clear cache and error to allow retry
                  setErrorLocal(null);
                  setQaData(null);
                  delete qaCacheRef.current[textId];
                  loadedTextIdRef.current = null;
                  loadingRef.current = false;
                }
              }}
              className="mt-4 px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
            >
              重試
            </button>
          </div>
        </div>
      );
    }
    
    if (!qaData) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-orange-600 via-orange-700 to-orange-800 text-white">
          <div className="max-w-6xl mx-auto p-6">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2">問答題精選 - {selectedText.title}</h1>
              <p className="text-orange-300">{selectedText.author} - {selectedText.dynasty}</p>
            </div>

            <div className="bg-white rounded-3xl p-8 text-gray-800">
              <div className="mb-6">
                <button
                  onClick={() => setCurrentView('home')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-xl hover:bg-gray-300 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>返回</span>
                </button>
              </div>

              <div className="text-center py-12">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-xl font-bold mb-2">問答題精選</h3>
                <p className="text-gray-600">此篇章的問答題精選內容正在準備中...</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-600 via-orange-700 to-orange-800 text-white">
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">{qaData.title}</h1>
            <p className="text-orange-300">{selectedText.author} - {selectedText.dynasty}</p>
          </div>

          <div className="bg-white rounded-3xl p-8 text-gray-800">
            <div className="mb-6">
              <button
                onClick={() => setCurrentView('home')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-xl hover:bg-gray-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回</span>
              </button>
            </div>

            <div className="space-y-8">
              {/* 段落重點整理 */}
              {qaData.sections.paragraphStructure && (
                <div className="bg-orange-50 rounded-2xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-orange-800">
                    <FileText className="w-5 h-5" />
                    {qaData.sections.paragraphStructure.title}
                  </h3>
                  <div className="space-y-4">
                    {qaData.sections.paragraphStructure.content.map((section, index) => (
                      <div key={index} className="bg-white rounded-xl p-4 shadow-sm">
                        <h4 className="font-bold text-orange-700 mb-3">{section.subtitle}</h4>
                        <div className="space-y-3">
                          {section.points.map((point, pointIndex) => (
                            <div key={pointIndex}>
                              <h5 className="font-semibold text-gray-800 mb-2">{point.title}</h5>
                              <ul className="list-disc list-inside space-y-1 text-gray-700">
                                {point.items.map((item, itemIndex) => (
                                  <li key={itemIndex} className="text-sm">{item}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 角色性格整理 */}
              {qaData.sections.characterAnalysis && (
                <div className="bg-blue-50 rounded-2xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-blue-800">
                    <Users className="w-5 h-5" />
                    {qaData.sections.characterAnalysis.title}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {qaData.sections.characterAnalysis.content.map((character, index) => (
                      <div key={index} className="bg-white rounded-xl p-4 shadow-sm">
                        <h4 className="font-bold text-blue-700 mb-2">{character.title}</h4>
                        <div className="flex flex-wrap gap-2">
                          {character.traits.map((trait, traitIndex) => (
                            <span key={traitIndex} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                              {trait}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 中心思想 */}
              {qaData.sections.centralTheme && (
                <div className="bg-green-50 rounded-2xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-green-800">
                    <Lightbulb className="w-5 h-5" />
                    {qaData.sections.centralTheme.title}
                  </h3>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      {qaData.sections.centralTheme.content.map((point, index) => (
                        <li key={index} className="text-sm">{point}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* 問答題精選 */}
              {qaData.sections.selectedQuestions && (
                <div className="bg-purple-50 rounded-2xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-purple-800">
                    <HelpCircle className="w-5 h-5" />
                    {qaData.sections.selectedQuestions.title}
                  </h3>
                  <div className="space-y-4">
                    {qaData.sections.selectedQuestions.questions.map((qa, index) => (
                      <div key={index} className="bg-white rounded-xl p-4 shadow-sm">
                        <div className="mb-3">
                          <span className="inline-block bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-medium mb-2">
                            問題 {index + 1}
                          </span>
                          <p className="font-semibold text-gray-800">{qa.question}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3">
                          <p className="text-sm text-gray-700">{qa.answer}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // FlashCards Completed View
  const FlashCardsCompletedView = () => (
    <div className="min-h-screen bg-gradient-to-br from-violet-600 via-violet-700 to-violet-800 text-white">
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <div className="text-8xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold mb-2">學習卡片完成！</h1>
          <h2 className="text-xl">恭喜你完成了所有卡片學習</h2>
        </div>

        <div className="bg-white rounded-3xl p-6 text-gray-800">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-bold mb-4">學習成果</h3>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-100 rounded-2xl p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{cardProgress.known}</div>
              <div className="text-sm text-green-700">已掌握</div>
            </div>
            <div className="bg-yellow-100 rounded-2xl p-4 text-center">
              <div className="text-3xl font-bold text-yellow-600">{cardProgress.learning}</div>
              <div className="text-sm text-yellow-700">學習中</div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-2xl p-4 text-center mb-6">
            <div className="text-4xl font-bold text-blue-600">
              {Math.round((cardProgress.known / (cardProgress.known + cardProgress.learning)) * 100) || 0}%
            </div>
            <div className="text-sm text-blue-700">掌握率</div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                setCurrentCard(0);
                setShowAnswer(false);
                setCardProgress({ known: 0, learning: 0 });
                setCurrentView('flashcards');
              }}
              className="w-full bg-violet-500 text-white py-3 rounded-xl font-semibold"
            >
              重新學習
            </button>
            <button
              onClick={() => setCurrentView('home')}
              className="w-full bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold"
            >
              返回首頁
            </button>
            <button
              onClick={() => openChat('drill', currentFlashCards)}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-3 rounded-xl font-semibold"
            >
              AI練習模式
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render current view
  const renderCurrentView = () => {
    switch (currentView) {
      case 'quiz':
        return <QuizView />;
      case 'quizCompleted':
        return <QuizCompletedView />;
      case 'flashcards':
        return <FlashCardsView />;
      case 'flashcardsCompleted':
        return <FlashCardsCompletedView />;
      case 'notes':
        return <NotesView />;
      case 'analysis':
        return <AnalysisView />;
      case 'qa':
        return <QAView />;
      default:
        return <HomeView />;
    }
  };

  return (
    <>
      {renderCurrentView()}
      
      {/* DeepSeek Chat Window */}
      <DeepSeekChatWindow
        isOpen={isChatOpen}
        onClose={closeChat}
        contextData={chatContext}
        initialMode={chatMode}
      />
    </>
  );
};

export default HKDSETexts; 